import { useEffect, useMemo, type FC } from 'react';
import { Icon } from 'src/common/components/icon';
import { cache } from 'src/redux/store';
import { WikiTocItemGroup } from './wiki-toc-item-group';
import { useWikiIds } from './use-wiki-ids';
import { useOpenModal } from '@flowus/common/next-modal';
import { CreateWikiPanel, WIKI_PANEL_ID } from './create-wiki-panel';
import { usePersonalWikiId } from './use-personal-wiki-id';
import { NavLink } from 'react-router-dom';
import { ViewPath } from 'src/utils';
import { useGetPageId } from 'src/utils/getPageId';
import { cx } from '@flowus/common/cx';
import { useSubscribePages } from 'src/services/app';
import { BlockStatus, BlockType, PermissionRole } from '@next-space/fe-api-idl';
import { useBlock } from 'src/hooks/block/use-block';
import { usePermissions } from 'src/hooks/share/use-permissions';
import { ILLEGAL_TEXT } from '@flowus/common/const-title';
import { SegmentPlainText } from '../toc/helper';
import { Images } from 'src/image';
import { IconTrigger } from 'src/components/icon-trigger';
import { NoAccess } from 'src/components/no-access';
import { segmentsToText } from 'src/editor/utils/editor';
import { getUntitledName } from 'src/utils/get-untitled-name';
import { Tooltip } from 'src/common/components/tooltip';
import { isMobile } from 'react-device-detect';
import { useToggleCollapse } from '../toc-trigger';

/** 订阅toc */
export const WikiToc: FC = () => {
  const personalWikiId = usePersonalWikiId();
  const wikiIds = useWikiIds();
  const openModal = useOpenModal();
  const pageId = useGetPageId();
  const subscribePages = useSubscribePages();
  const toggleCollapse = useToggleCollapse();
  const joinWikiIds = useMemo(() => {
    return subscribePages?.filter((id) => {
      const page = cache.blocks[id];
      return page && page.type === BlockType.WIKI;
    });
  }, [subscribePages]);

  useEffect(() => {
    if (isMobile) {
      toggleCollapse(true);
    }
  }, [toggleCollapse]);

  return (
    <div className="h-full min-h-17.5 overflow-y-auto pb-10">
      <NavLink
        className={cx('px-2 flex items-center mx-2 animate-hover h-8 space-x-1', {
          'bg-black_006': personalWikiId === pageId,
          'h-10': isMobile,
        })}
        to={`${ViewPath.wiki}/${personalWikiId}`}
        onClick={() => {
          if (isMobile) {
            toggleCollapse(true);
          }
        }}
      >
        {/* <div className="px-2 flex items-center mx-2 animate-hover h-8 space-x-1" onClick={() => {}}> */}
        <Icon className="text-grey3" name={'IcFavorite'} size="middle" />
        <span className="text-t2-medium text-grey2">个人知识库</span>
        {/* </div> */}
      </NavLink>
      <div className="px-2 flex items-center mx-2 h-8 space-x-1 mt-5 select-none">
        <Icon className="text-grey3" name={'IcLightbulb'} size="middle" />
        <span className="text-t2-medium text-grey2">共享知识库</span>
      </div>
      <WikiTocItemGroup
        name="我创建的"
        onAddWiki={() => {
          openModal.modal({
            modalId: WIKI_PANEL_ID,
            content: () => <CreateWikiPanel />,
          });
        }}
      >
        {wikiIds.map((id, index) => {
          const page = cache.blocks[id];
          if (!page) return null;
          return <WikiTocItem uuid={id} key={index} />;
        })}
        {!wikiIds.length && (
          <div className="text-t4 text-grey3 text-center flex items-center justify-center flex-col py-1">
            <span>暂无内容，快去创建知识库吧～</span>
          </div>
        )}
      </WikiTocItemGroup>
      <WikiTocItemGroup name="我加入的">
        {joinWikiIds?.map((id, index) => {
          const page = cache.blocks[id];
          if (!page) return null;
          return <WikiTocItem uuid={id} key={index} />;
        })}
        {!joinWikiIds?.length && (
          <div className="text-t4 text-grey3 text-center flex items-center justify-center flex-col py-1">
            <span>暂无内容，快去加入知识库吧～</span>
          </div>
        )}
      </WikiTocItemGroup>
    </div>
  );
};

export const WikiTocItem: FC<{ uuid: string; className?: string; onClick?: () => void }> = (
  props
) => {
  const block = useBlock(props.uuid);
  const { role, illegal } = usePermissions(props.uuid);
  // 只要没数据或者没权限，就一律不显示到目录树
  const deleted = role === PermissionRole.NONE || block?.status === BlockStatus.DELETED_COMPLETE;
  const popup = useMemo(
    () =>
      illegal ? ILLEGAL_TEXT : segmentsToText(block?.data.segments) || getUntitledName(block?.type),
    [block?.data.segments, block?.type, illegal]
  );
  const toggleCollapse = useToggleCollapse();
  const isSelected = useGetPageId() === props.uuid;
  if (deleted) return null;
  return (
    <Tooltip
      className={cx('relative block animate-fade-in', props.className)}
      placement="right"
      popup={isMobile ? '' : popup}
      delay={1000}
    >
      <NavLink
        to={`${ViewPath.wiki}/${props.uuid}`}
        activeClassName={isMobile ? undefined : 'normal-bg'}
        className={cx(
          'rounded flex items-center relative h-8 border-transparent group mx-2 border text-t2-medium text-grey2 space-x-1 pr-2 ',
          {
            'opacity-30': illegal,
            'h-12': isMobile,
            'animate-hover-black3': !isMobile,
          }
        )}
        style={{ paddingLeft: isMobile ? 4 : 20 }}
        onClick={() => {
          if (isMobile) {
            toggleCollapse(true);
            props.onClick?.();
          }
        }}
      >
        <div className="w-1" />
        {block ? (
          <>
            <IconTrigger
              trigger={false}
              iconSize={18}
              blockId={props.uuid}
              hasOverlay={false}
              className={cx('w-[18px] h-[18px] text-grey3')}
              placement="bottom-start"
              defaultIcon={<img className="w-5 h-5" src={Images.wikiDefaultIcon} />}
            />

            <div className="block flex-1 pl-1 text-ellipsis">
              {illegal ? ILLEGAL_TEXT : <SegmentPlainText uuid={props.uuid} />}
            </div>
          </>
        ) : (
          <NoAccess id={props.uuid} disable={true} className="w-full" />
        )}
        {isSelected && <Icon name="IcSelectedSpace" className="text-active_color" size="middle" />}
      </NavLink>
    </Tooltip>
  );
};
